<template>
  <div>
    <div ref="mapEl" class="base-pin-map"></div>
    <p class="small text-muted mt-1 mb-0">Arraste o pino (ou clique no mapa) até o ponto exato de saída.</p>
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Mapa com pino arrastável para marcar a base de saída das entregas.
// Uso: <BasePinMap :lat="x" :lng="y" @update="({lat, lng}) => ..." />
export default {
  name: 'BasePinMap',
  props: {
    lat: { type: [Number, String], default: null },
    lng: { type: [Number, String], default: null },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const mapEl = ref(null);
    let map = null;
    let marker = null;
    let timerResize = null;

    // Centro do Brasil quando ainda não há ponto
    const FALLBACK = [-15.78, -47.93];

    function ponto() {
      return props.lat != null && props.lat !== '' ? [Number(props.lat), Number(props.lng)] : null;
    }

    function mover(latlng, avisar = true) {
      if (!marker) {
        marker = L.marker(latlng, { draggable: true }).addTo(map);
        marker.on('dragend', () => {
          const p = marker.getLatLng();
          emit('update', { lat: Number(p.lat.toFixed(7)), lng: Number(p.lng.toFixed(7)) });
        });
      } else {
        marker.setLatLng(latlng);
      }
      if (avisar) {
        emit('update', { lat: Number(latlng.lat.toFixed(7)), lng: Number(latlng.lng.toFixed(7)) });
      }
    }

    onMounted(() => {
      const inicial = ponto();
      map = L.map(mapEl.value).setView(inicial || FALLBACK, inicial ? 16 : 4);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
      }).addTo(map);
      if (inicial) mover(L.latLng(inicial), false);
      map.on('click', (e) => mover(e.latlng));
      // Recalcula o tamanho depois do layout assentar (o mapa pode ter nascido oculto)
      timerResize = setTimeout(() => {
        if (map) map.invalidateSize();
      }, 100);
    });

    // Ponto alterado por fora (ex.: botão "Localizar pelo CEP")
    watch(() => [props.lat, props.lng], () => {
      const p = ponto();
      if (!map || !p) return;
      const atual = marker?.getLatLng();
      if (!atual || atual.lat !== p[0] || atual.lng !== p[1]) {
        mover(L.latLng(p), false);
        map.setView(p, 16);
      }
    });

    // A tela pode desmontar o mapa antes do timer (ex.: recarregar dados): sem isso o
    // invalidateSize roda num mapa já removido e o Leaflet quebra (_leaflet_pos)
    onBeforeUnmount(() => {
      clearTimeout(timerResize);
      if (map) map.remove();
      map = null;
      marker = null;
    });

    return { mapEl };
  }
}
</script>

<style scoped>
.base-pin-map {
  height: 300px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}
</style>
